/**
 * State for the S3 blueprint editor.
 *
 * Edits accumulate in a local draft that starts as a copy of the saved version, so
 * Save sends one PATCH for the whole blueprint and Publish always acts on persisted
 * state. Published versions are immutable server-side; `readOnly` mirrors that so the
 * UI never offers an edit that the API would reject.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getVersion,
  listIndustryPacks,
  listPromptBindings,
  publishVersion,
  updateVersion,
} from '@/services/aiStudioAgentService';
import type {
  AiStudioAgentVersion,
  AiStudioIndustryPack,
  AiStudioPromptBinding,
  UpdateVersionRequest,
} from '@/types/aiStudioAgent';

/** The saved version projected onto the editable subset. */
function toDraft(version: AiStudioAgentVersion): Required<UpdateVersionRequest> {
  return {
    enabledStages: version.enabledStages,
    dataSchema: version.dataSchema,
    outputSchema: version.outputSchema,
    benchmarkConfig: version.benchmarkConfig,
    opportunityLogic: version.opportunityLogic,
    engineConfig: version.engineConfig,
    promotionRules: version.promotionRules,
    // Claim quality = defaultDecisionMode; execution stays propose+approve (HITL).
    executionMode: 'propose_approve',
    autonomyFences: version.autonomyFences,
    defaultDecisionMode: version.defaultDecisionMode ?? 'PROVISIONAL',
    contextContract: version.contextContract,
    computeClasses: version.computeClasses,
    archetype: version.archetype,
    industryPackId: version.industryPackId,
    inputExpected: version.inputExpected,
    outputExpected: version.outputExpected,
    contextDefinition: version.contextDefinition,
    explainInstructions: version.explainInstructions,
    successCriteria: version.successCriteria,
    hitlRequired: true,
  };
}

export interface VersionEditorState {
  version: AiStudioAgentVersion | null;
  draft: Required<UpdateVersionRequest> | null;
  packs: AiStudioIndustryPack[];
  promptBindings: AiStudioPromptBinding[];
  loading: boolean;
  saving: boolean;
  publishing: boolean;
  dirty: boolean;
  readOnly: boolean;
  /** True when every JSON panel currently holds parseable JSON. */
  jsonValid: boolean;
  errorKey: string | null;
  applyPatch: (patch: UpdateVersionRequest) => void;
  setJsonValidity: (key: string, valid: boolean) => void;
  save: () => Promise<void>;
  publish: () => Promise<void>;
  setPromptBindings: (bindings: AiStudioPromptBinding[]) => void;
}

export function useVersionEditor(versionId: string | undefined): VersionEditorState {
  const [version, setVersion] = useState<AiStudioAgentVersion | null>(null);
  const [draft, setDraft] = useState<Required<UpdateVersionRequest> | null>(null);
  const [packs, setPacks] = useState<AiStudioIndustryPack[]>([]);
  const [promptBindings, setPromptBindings] = useState<AiStudioPromptBinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [invalidJsonFields, setInvalidJsonFields] = useState<string[]>([]);
  // Errors are held as translation keys so effects never depend on `t`.
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (!versionId) return;
    let cancelled = false;
    setLoading(true);
    setErrorKey(null);
    Promise.all([getVersion(versionId), listPromptBindings(versionId), listIndustryPacks()])
      .then(([loadedVersion, bindings, loadedPacks]) => {
        if (cancelled) return;
        setVersion(loadedVersion);
        setDraft(toDraft(loadedVersion));
        setPromptBindings(bindings);
        setPacks(loadedPacks);
        setDirty(false);
        setInvalidJsonFields([]);
      })
      .catch(() => {
        if (!cancelled) setErrorKey('aiStudio.versions.loadFailed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [versionId]);

  const applyPatch = useCallback((patch: UpdateVersionRequest) => {
    setDraft((current) => (current ? { ...current, ...patch } : current));
    setDirty(true);
  }, []);

  const setJsonValidity = useCallback((key: string, valid: boolean) => {
    setInvalidJsonFields((current) => {
      const without = current.filter((field) => field !== key);
      return valid ? without : [...without, key];
    });
  }, []);

  const save = useCallback(async () => {
    if (!versionId || !draft) return;
    setSaving(true);
    setErrorKey(null);
    try {
      const saved = await updateVersion(versionId, draft);
      setVersion(saved);
      setDraft(toDraft(saved));
      setDirty(false);
      setInvalidJsonFields([]);
    } catch {
      setErrorKey('aiStudio.versions.saveFailed');
    } finally {
      setSaving(false);
    }
  }, [draft, versionId]);

  const publish = useCallback(async () => {
    if (!versionId) return;
    setPublishing(true);
    setErrorKey(null);
    try {
      // Publish after persisting, otherwise unsaved blueprint edits are silently lost
      // against an immutable published version.
      if (dirty && draft) {
        const saved = await updateVersion(versionId, draft);
        setVersion(saved);
        setDraft(toDraft(saved));
        setDirty(false);
      }
      const published = await publishVersion(versionId);
      setVersion(published);
      setDraft(toDraft(published));
      setInvalidJsonFields([]);
    } catch {
      setErrorKey('aiStudio.versions.publishFailed');
    } finally {
      setPublishing(false);
    }
  }, [dirty, draft, versionId]);

  const readOnly = version !== null && version.lifecycleStatus !== 'draft';

  return useMemo(
    () => ({
      version,
      draft,
      packs,
      promptBindings,
      loading,
      saving,
      publishing,
      dirty,
      readOnly,
      jsonValid: invalidJsonFields.length === 0,
      errorKey,
      applyPatch,
      setJsonValidity,
      save,
      publish,
      setPromptBindings,
    }),
    [
      applyPatch,
      draft,
      dirty,
      errorKey,
      invalidJsonFields.length,
      loading,
      packs,
      promptBindings,
      publish,
      publishing,
      readOnly,
      save,
      saving,
      setJsonValidity,
      version,
    ]
  );
}
